import { useMemo } from "react";

/**
 * Native date input + YEAR and MONTH jump dropdowns. The native picker
 * is great for the DAY portion but on mobile makes you scroll
 * month-by-month, painful when a tech is entering a 15-year-old
 * equipment startup date. The dropdowns let either field be set in one
 * tap; the native picker still handles the day.
 *
 * Value is the standard YYYY-MM-DD string. Empty string = no value.
 *
 * Props:
 *   - value: current date string ("" or YYYY-MM-DD)
 *   - onChange: receives the next YYYY-MM-DD string
 *   - yearsBack: how far back the year dropdown reaches (default 50).
 *     Equipment install dates can go back decades; PM dates usually
 *     don't, so callers can shrink this for cleaner UI.
 *   - yearsAhead: how far ahead (default 1). Use 0 for past-only fields.
 */
const MONTHS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

export default function DateWithYearJump({
  value = "",
  onChange,
  yearsBack = 50,
  yearsAhead = 1,
}) {
  const currentYear = new Date().getFullYear();
  const years = useMemo(() => {
    const list = [];
    for (let y = currentYear + yearsAhead; y >= currentYear - yearsBack; y--) {
      list.push(y);
    }
    return list;
  }, [currentYear, yearsBack, yearsAhead]);

  const currentYearInValue = value ? value.slice(0, 4) : "";
  const currentMonthInValue = value && value.length >= 7 ? value.slice(5, 7) : "";

  // Replace just the year portion. If no value yet, seed with Jan 1 so
  // the date input has something to render and the user can fine-tune
  // day from there. Same idea for month.
  const onYearChange = (e) => {
    const newYear = e.target.value;
    if (!newYear) {
      onChange("");
      return;
    }
    if (value && value.length >= 10) {
      onChange(`${newYear}${value.slice(4)}`);
    } else {
      onChange(`${newYear}-01-01`);
    }
  };

  const onMonthChange = (e) => {
    const newMonth = e.target.value;
    if (!newMonth) {
      if (value) onChange("");
      return;
    }
    if (value && value.length >= 10) {
      onChange(`${value.slice(0, 5)}${newMonth}${value.slice(7)}`);
    } else {
      // No existing value — seed with current year, picked month, day 1.
      onChange(`${currentYear}-${newMonth}-01`);
    }
  };

  return (
    <span
      style={{
        display: "inline-flex",
        gap: "6px",
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      <select
        value={currentYearInValue}
        onChange={onYearChange}
        style={{ padding: "2px 6px" }}
        title="Jump to year"
      >
        <option value="">Year…</option>
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
      <select
        value={currentMonthInValue}
        onChange={onMonthChange}
        style={{ padding: "2px 6px" }}
        title="Jump to month"
      >
        <option value="">Month…</option>
        {MONTHS.map((m) => (
          <option key={m.value} value={m.value}>
            {m.label}
          </option>
        ))}
      </select>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </span>
  );
}
